import React, { useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import adrActions from "../../../_actions/adrActions";
import { actionScripts } from "../../../_helpers/actionScripts";
import mainTabActions from "../../../_actions/mainTabActions";
import AlertModal from "../../_common/AlertModal";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
// import AdrClarificationComments from "./adrClarificationComments";
import "../styles/adrReviewMode.css";
// import AdrReviewSuccessModal from "./AdrReviewSuccessModal";
import { AdrReviewHeaderV3 } from "./AdrReviewHeaderV3";
import { AdrReviewNarrativesV3 } from "./AdrReviewNarrativesV3";
import { AdrReviewGeneralCommentsV3 } from "./AdrReviewGeneralCommentsV3";
import { AdrReviewLaborSetV3 } from "./AdrReviewLaborSetV3";
import { AdrReviewEquipmentSetV3 } from "./AdrReviewEquipmentSetV3";
import AdrClarificationCommentsV3 from "./AdrClarificationCommentsV3";
import AdrReviewSuccessModalV3 from "./AdrReviewSuccessModalV3";
import { getInitials } from "../../../_utils/generalUtils";
import AdrReviewTentativeAgreementsV3 from "./AdrReviewTentativeAgreementsV3";
import AdrAmendmentHistoryV3 from "../AdrAmendmentHistoryV3";
import userActions from "../../../_actions/userActions";
import { docPermConstants } from "../../../_constants/permissionConstants";

export default function AdrReviewV3(props) {
  const { tabId, projectId, status, markAdrDirty, markAdrClean, userIsReviewer, permissions } = props;

  const dispatch = useDispatch();
  const user = useSelector((state) => state.authentication.user);
  const adr = useSelector((state) => state.adr.openAdrs[tabId] || null);
  const miscSessionData = useSelector((state) => state.adr.miscSessionData || null);
  const jobStamp = useSelector((state) => state.projects.jobStamps[projectId] || null);

  const [isAccepting, setIsAccepting] = useState(false);
  const [isRejecting, setIsRejecting] = useState(false);
  const [isSavingReview, setIsSavingReview] = useState(false);

  const [showAlert_saveReviewSuccess, setShowAlert_saveReviewSuccess] = useState(false);
  const toggleAlert_saveReviewSuccess = () =>
    setShowAlert_saveReviewSuccess(!showAlert_saveReviewSuccess);

  const [showAlert_reviewSuccess, setShowAlert_reviewSuccess] = useState(false);
  const toggleAlert_reviewSuccess = () =>
    setShowAlert_reviewSuccess(!showAlert_reviewSuccess);

  const [showAlert_rejectConfirm, setShowAlert_rejectConfirm] = useState(false);
  const toggleAlert_rejectConfirm = () =>
    setShowAlert_rejectConfirm(!showAlert_rejectConfirm);

  const [showAlert_noMoreToReview, setShowAlert_noMoreToReview] = useState(false);
  const toggleAlert_noMoreToReview = () =>
    setShowAlert_noMoreToReview(!showAlert_noMoreToReview);

  useEffect(() => {
    const accept = async () => {
      await dispatch(adrActions.approve(tabId, user.userId));
      setIsAccepting(false);
      dispatch(adrActions.fetchAmendmentHistory(adr.assistantDailyReportId));
      toggleAlert_reviewSuccess();
    };
    if (isAccepting) accept();
  }, [isAccepting]);

  useEffect(() => {
    const reject = async () => {
      await dispatch(adrActions.reject(tabId, user.userId));
      setIsRejecting(false);
      dispatch(adrActions.fetchAmendmentHistory(adr.assistantDailyReportId));
      toggleAlert_reviewSuccess();
    };
    if (isRejecting) reject();
  }, [isRejecting]);

  useEffect(() => {
    const save = async () => {
      await dispatch(adrActions.saveReview(tabId));
      setIsSavingReview(false);
      dispatch(adrActions.fetchAmendmentHistory(adr.assistantDailyReportId));
      toggleAlert_saveReviewSuccess();
    };
    if (isSavingReview) save();
  }, [isSavingReview]);

  const handleAlert_rejectConfirm = (actionType) => (e) => {
    e.preventDefault();
    if (actionType === "accept") setIsRejecting(true);
    else toggleAlert_rejectConfirm();
  };

  const handleAlert_reviewSuccess = async (selectedAction) => {
    switch (selectedAction) {
      case "closeAdr":
        toggleAlert_reviewSuccess();
        dispatch(mainTabActions.closeTab(tabId));
        break;
      case "openNext":
        try {
          const adrId = await dispatch(adrActions.fetchNextToReview(tabId));
          toggleAlert_reviewSuccess();
          if (adrId) actionScripts.FETCH_ADR.run(projectId, adrId);
          else {
            this.toggleAlert_noMoreToReview();
            dispatch(mainTabActions.closeTab(tabId));
          }
        } catch (e) {
          toggleAlert_reviewSuccess();
        }
        break;
      default:
        break;
    }
  };

  const handleAlert_saveReview = (actionType) => (e) => {
    e.preventDefault();
    window.scrollTo(0, 0);
    if (actionType === "accept") {
      actionScripts.REFETCH_ADR_IN_TAB.run(projectId, adr.assistantDailyReportId, tabId);
      toggleAlert_saveReviewSuccess();
    }
  };

  const wpSortOrder = useMemo(
    () =>
      adr.workPerformedClient && Object.keys(adr.workPerformedClient).length > 0
        ? dispatch(adrActions.getWorkPerformedSortOrder(tabId))
        : null,
    [adr]
  );

  const reviewerInitials = useMemo(() => getInitials(user.fullName) || "", [user]);

  const originalAdrId = useMemo(
    () =>
      adr
        ? adr.isAmendment
          ? adr.originalAssistantDailyReportId
          : adr.assistantDailyReportId
        : null,
    [adr]
  );

  const amendmentHistory = useMemo(() => {
    return (
      (originalAdrId && miscSessionData.amendmentHistory[originalAdrId]) || null || null
    );
  }, [originalAdrId, adr]);

  // Repackage props to pass down to avoid passing heavier items like adr
  const propsToPass = useMemo(
    () => ({
      projectId,
      tabId,
      markAdrDirty,
      isInReview: adr.isInReview,
      reviewerInitials,
      userIsReviewer: status.userIsReviewer,
    }),
    [projectId, tabId, markAdrDirty, adr, reviewerInitials, status]
  );

  const renderAdrTitle = () => {
    return (
      <div className="row mb-0">
        <div className="col-9">
          <h5 className="text-muted">ASSISTANT RE/SR DAILY REPORT</h5>
        </div>
        <div className="col-3">
          <small>
            <b>Correction Comments</b>
          </small>
        </div>
      </div>
    );
  };

  return (
    <React.Fragment>
      <AdrReviewStatusV3 adr={adr} tabId={tabId} permissions={permissions}/>
      <div className="card m-2">
        <div className="card-body">
          {amendmentHistory &&
            (adr.isAmendment || (amendmentHistory || []).length > 1) && (
              <AdrAmendmentHistoryV3 tabId={tabId} originalAdrId={originalAdrId} />
            )}
          {renderAdrTitle()}
          <AdrReviewHeaderV3
            {...propsToPass}
            jobStamp={<AdrReviewJobStampV3 jobStamp={jobStamp} />}
          />
          <AdrReviewHrV3 />
          <AdrReviewNarrativesV3 {...propsToPass} wpSortOrder={wpSortOrder} />
          <AdrReviewHrV3 />
          <AdrReviewLaborSetV3 {...propsToPass} wpSortOrder={wpSortOrder} />
          <AdrReviewHrV3 />
          <AdrReviewEquipmentSetV3 {...propsToPass} wpSortOrder={wpSortOrder} />
          <AdrReviewHrV3 />
          <AdrReviewGeneralCommentsV3 {...propsToPass} />
          {adr && !adr.isCem4501 && (
            <React.Fragment>
              <AdrReviewHrV3 />
              <AdrReviewTentativeAgreementsV3
                {...propsToPass}
                status={status}
                markAdrClean={markAdrClean}
              />
            </React.Fragment>
          )}
        </div>
      </div>
      {adr && !adr.isDraft && <AdrClarificationCommentsV3 {...propsToPass} />}
      <div className="card mx-2 mb-2">
        <div className="card-body bg-lightgray">
          <div className="form-row float-right">
            <button
              className="btn btn-secondary mr-2"
              name="saveReview"
              id="saveReview"
              onClick={() => setIsSavingReview(true)}
              disabled={!userIsReviewer || isRejecting || isAccepting || isSavingReview}
            >
              {isSavingReview && (
                <span className="mr-2 mt-1">
                  <FontAwesomeIcon
                    icon="spinner"
                    className="loading-spinner"
                    style={{ color: "white" }}
                  />
                </span>
              )}
              {adr.isInReview && userIsReviewer
                ? "Save Review Progress"
                : "Save Comments"}
            </button>
            <span className="mx-2">|</span>
            <button
              className="btn btn-danger mx-2"
              name="reject"
              id="reject"
              onClick={toggleAlert_rejectConfirm}
              disabled={
                !userIsReviewer ||
                !adr.isInReview ||
                adr.isReviewRevoked ||
                isRejecting ||
                isAccepting ||
                isSavingReview
              }
            >
              {isRejecting && (
                <span className="mr-2 mt-1">
                  <FontAwesomeIcon
                    icon="spinner"
                    className="loading-spinner"
                    style={{ color: "white" }}
                  />
                </span>
              )}
              Reject
            </button>
            <button
              className="btn btn-primary mr-2"
              name="accept"
              id="accept"
              onClick={() => setIsAccepting(true)}
              disabled={
                !userIsReviewer ||
                adr.isReviewRevoked ||
                !adr.isInReview ||
                isRejecting ||
                isAccepting ||
                isSavingReview
              }
            >
              {isAccepting && (
                <span className="mr-2 mt-1">
                  <FontAwesomeIcon
                    icon="spinner"
                    className="loading-spinner"
                    style={{ color: "white" }}
                  />
                </span>
              )}
              <b>Accept</b>
            </button>
          </div>
        </div>
      </div>
      {showAlert_reviewSuccess && (
        <AdrReviewSuccessModalV3
          showAlert_reviewSuccess={showAlert_reviewSuccess}
          toggleAlert_reviewSuccess={toggleAlert_reviewSuccess}
          handleAlertAction={handleAlert_reviewSuccess}
          isApproved={adr.isApproved}
        />
      )}
      {showAlert_rejectConfirm && (
        <AlertModal
          alertTitle="Confirm Rejection"
          alertTitleIcon="exclamation-triangle"
          alertTitleClass="bg-warning"
          alertText="Are you sure you want to reject this daily report?"
          acceptCaption="Yes"
          acceptClass="danger"
          provideReject={true}
          rejectCaption="No"
          rejectClass="secondary"
          handleAlertAction={handleAlert_rejectConfirm}
          toggleAlertModal={toggleAlert_rejectConfirm}
          showAlertModal={showAlert_rejectConfirm}
        />
      )}
      {showAlert_noMoreToReview && (
        <AlertModal
          alertTitle="Review Complete"
          alertTitleIcon="check"
          alertTitleClass="bg-success text-white"
          alertText="There are no more daily reports to review."
          acceptCaption="OK"
          acceptClass="primary"
          provideReject={false}
          handleAlertAction={toggleAlert_noMoreToReview}
          toggleAlertModal={toggleAlert_noMoreToReview}
          showAlertModal={showAlert_noMoreToReview}
        />
      )}
      {showAlert_saveReviewSuccess && (
        <AlertModal
          alertTitle="Saved"
          alertTitleIcon="check"
          alertTitleClass="bg-success text-white"
          alertText="Your review progress has been saved."
          acceptCaption="OK"
          acceptClass="primary"
          provideReject={false}
          handleAlertAction={handleAlert_saveReview}
          toggleAlertModal={toggleAlert_saveReviewSuccess}
          showAlertModal={showAlert_saveReviewSuccess}
        />
      )}
    </React.Fragment>
  );
}

function AdrReviewHrV3() {
  return (
    <div className="row mb-0">
      <div className="col-12 px-1">
        <hr />
      </div>
    </div>
  );
}

function AdrReviewStatusV3(props) {
  const { adr, tabId, permissions } = props;
  

  const dispatch = useDispatch();
  const user = useSelector((state) => state.authentication.user);

  const [isStartingReview, setIsStartingReview] = useState(false);
  const [reviewerName, setReviewerName] = useState("");

  const [showAlert_reviewRevoked, setShowAlert_reviewRevoked] = useState(false);
  const toggleAlert_reviewRevoked = () =>
    setShowAlert_reviewRevoked(!showAlert_reviewRevoked);

  useEffect(() => {
    if (adr.isReviewRevoked) toggleAlert_reviewRevoked();
  }, [adr.isReviewRevoked]);

  useEffect(() => {
    const getReviewerName = async (reviewerUserId) => {
      const name = await dispatch(userActions.getName(reviewerUserId));
      setReviewerName(name);
    };
    if (adr.isInReview) getReviewerName(adr.reviewerUserId);
  }, [adr.isInReview, adr.reviewerUserId]);

  useEffect(() => {
    const startReview = async () =>
      await dispatch(
        adrActions.startReview(tabId, adr.assistantDailyReportId, user.userId)
      );
    if (isStartingReview) {
      startReview();
      setIsStartingReview(false);
    }
  }, [isStartingReview, adr, user]);

  return (
    <>
      {!permissions.includes(docPermConstants.PROJECT_APPROVE) && (
        <div className="d-flex flex-row w-100">
          <div className="alert alert-danger mx-0 mb-2 p-2 w-100">
            <FontAwesomeIcon icon="exclamation-triangle" className="fa-fw mr-2" />
            You do not have sufficient privileges to review reports.
          </div>
        </div>
      )}
      {adr.isSubmitted && (
        <div className="card m-2 bg-lightyellow">
          <div className="card-body p-2">
            {adr.isInReview ? (
              <>
                <span>
                  Currently being reviewed by <b>{reviewerName}</b>
                </span>
                {adr.reviewerUserId != user.userId && (
                  <button
                    name="transferReview"
                    id="transferReview"
                    className="btn btn-primary ml-2"
                    onClick={() => setIsStartingReview(true)}
                  >
                    <b>Transfer Review to Me</b>
                  </button>
                )}
              </>
            ) : (
              <>
                <div>
                  <button
                    name="startReview"
                    id="startReview"
                    className="btn btn-primary"
                    onClick={() => setIsStartingReview(true)}
                    disabled={!permissions.includes(docPermConstants.PROJECT_APPROVE)}
                  >
                    <b>Start Review</b>
                  </button>
                </div>
              </>
            )}
          </div>
        </div>
      )}
      {showAlert_reviewRevoked && (
        <AlertModal
          alertTitle="Review Transferred"
          alertTitleIcon="exclamation-triangle"
          alertTitleClass="bg-warning"
          alertText={
            `Another reviewer has begun review of the Daily Report written by ${
              adr.authorFullName
            }, dated ${new Date(
              adr.reportStartDate
            ).toLocaleDateString()}, and you are no longer marked as the current reviewer.\n` +
            "Your current review progress has been saved. If you need to continue your review, you can transfer the review back to yourself."
          }
          acceptCaption="OK"
          acceptClass="secondary"
          provideReject={false}
          handleAlertAction={toggleAlert_reviewRevoked}
          toggleAlertModal={toggleAlert_reviewRevoked}
          showAlertModal={showAlert_reviewRevoked}
        />
      )}
    </>
  );
}

function AdrReviewJobStampV3(props) {
  const { jobStamp } = props;
  return (
    <div className="card">
      <div className="card-body p-3">
        <span>
          <small>
            <b>JOBSTAMP</b>
          </small>
        </span>
        <br />
        {jobStamp ? (
          <>
            {jobStamp.fullEA}
            <br />
            {jobStamp.efis}
            <br />
            {jobStamp.distCoRtePm}
            <br />
            {jobStamp.fedId}
            <br />
            {jobStamp.workDescription}
            <br />
            {jobStamp.locationDescription}
          </>
        ) : (
          <>(JobStamp N/A)</>
        )}
      </div>
    </div>
  );
}

